import { AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import type { Category, RecipeIngredient } from '../../lib/supabase'
import type { KnownName } from '../../lib/catalog'
import type { ReviewRow } from '../../lib/importReview'
import { readAmount } from '../../lib/recipeDraft'
import { NameField } from '../choices/NameField'
import { CategoryPills } from '../CategoryPills'
import { SheetPanel } from '../SheetPanel'

// After an import, the few things it couldn't settle by itself
// (lib/importReview.ts reviewRows) — everything else it already filled in.
// A row can ask about its name (renamed to the household's, or maybe one it
// uses: take back, take, or change), its amount (a line the parser had to
// guess at, shown as the page wrote it) and its aisle (nobody has filed it and
// no keyword knows it). Done saves what changed; a picked aisle is kept on the
// ingredient, so Add to list files it there.

export type { ReviewRow }

/** What changed per ingredient, by its index in the recipe. */
export type ReviewChanges = Map<number, Partial<RecipeIngredient>>

interface Props {
  /** The imported recipe and what to ask about it; null when closed. */
  review: { title: string; rows: ReviewRow[] } | null
  known: KnownName[]
  categories: Category[]
  tintFor: (categoryId: string | null) => string
  onSave: (changes: ReviewChanges) => void
  onClose: () => void
}

export function ImportReviewSheet({ review, onClose, ...rest }: Props) {
  return (
    <AnimatePresence>
      {review && (
        <SheetPanel onClose={onClose} ariaLabel="Check the import" maxHeight="92dvh" layout="scroll">
          <Body review={review} onClose={onClose} {...rest} />
        </SheetPanel>
      )}
    </AnimatePresence>
  )
}

/** Renamed to another word, not just another case ("mjölk" → "Mjölk"). */
const renamed = (r: ReviewRow) => r.to.toLocaleLowerCase() !== r.from.toLocaleLowerCase()

interface RowState { name: string; amount: string; categoryId: string | null }

function Body({ review, known, categories, tintFor, onSave, onClose }: Props & { review: NonNullable<Props['review']> }) {
  const [rows, setRows] = useState<RowState[]>(() => review.rows.map(r => ({ name: r.to, amount: r.amount, categoryId: r.categoryId })))
  const [editing, setEditing] = useState<number | null>(null)
  const set = (k: number, patch: Partial<RowState>) => setRows(rs => rs.map((r, i) => (i === k ? { ...r, ...patch } : r)))

  function done() {
    const changes: ReviewChanges = new Map()
    review.rows.forEach((r, k) => {
      const now = rows[k]
      const change: Partial<RecipeIngredient> = {}
      let name = now.name.trim().replace(/\s+/g, ' ') || r.from
      if (r.unclear && now.amount.trim() !== r.amount) {
        const read = readAmount(now.amount)
        change.amount = read.amount
        change.unit = read.unit
        if (read.rest) name = `${read.rest} ${name}`
      }
      if (name !== r.to) change.name = name
      if (now.categoryId && now.categoryId !== r.categoryId) change.category_id = now.categoryId
      if (Object.keys(change).length) changes.set(r.index, change)
    })
    if (changes.size) onSave(changes)
    onClose()
  }

  const count = review.rows.length
  const matched = review.rows.filter(renamed).length
  return (
    <div className="ml-import-names">
      <p className="ml-label">{review.title}</p>
      <h2 className="ml-display ml-sheet-title">{count === 1 ? 'One thing' : `${count} things`} to check</h2>
      <p className="ml-caption">
        {matched > 0 && `${matched === 1 ? 'One ingredient was' : `${matched} ingredients were`} named the way your list already does, so they add to the list as one thing. `}
        Everything else is filled in.
      </p>
      <ul className="ml-import-names__rows">
        {review.rows.map((r, k) => {
          const now = rows[k]
          const kept = now.name === r.from
          const asksName = renamed(r) || r.maybe.length > 0
          return (
            <li key={r.index} className="ml-import-names__row">
              {r.unclear && <p className="ml-caption">The recipe says “{r.raw}”</p>}
              {editing === k
                ? (
                  <form className="ml-choices__editor" aria-label={`Name for ${r.from}`}
                    onSubmit={e => { e.preventDefault(); setEditing(null) }}>
                    <NameField label={`Name for “${r.from}”`} value={now.name} known={known} onChange={n => set(k, { name: n })} autoFocus />
                    <div className="ml-choices__editor-actions">
                      <button type="submit" className="ml-btn ml-btn--primary">OK</button>
                    </div>
                  </form>
                )
                : asksName
                  ? (
                    <>
                      <p className="ml-import-names__pair">Recipe says <strong>{r.from}</strong></p>
                      <p className="ml-import-names__pair">
                        {kept ? 'Kept as the recipe says' : <>Goes on the list as <strong>{now.name}</strong></>}
                      </p>
                      <div className="ml-import-names__actions">
                        {kept && r.maybe.length > 0 && <span className="ml-caption">Maybe the same as</span>}
                        {kept && r.maybe.map(n => (
                          <button key={n} type="button" className="ml-text-btn" onClick={() => set(k, { name: n })}>{n}</button>
                        ))}
                        {kept && r.to !== r.from && <button type="button" className="ml-text-btn" onClick={() => set(k, { name: r.to })}>Use “{r.to}”</button>}
                        {!kept && <button type="button" className="ml-text-btn" onClick={() => set(k, { name: r.from })}>Keep “{r.from}”</button>}
                        <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={() => setEditing(k)}
                          aria-label={`Change the name for ${r.from}`}>Change</button>
                      </div>
                    </>
                  )
                  : (
                    // A name new to the household can be respelled too ("tagliate le" off a photo).
                    <div className="ml-import-names__actions">
                      <p className="ml-import-names__pair"><strong>{now.name}</strong>{!r.unclear && r.amount && <span className="ml-caption ml-import-names__amount-text">{r.amount}</span>}</p>
                      <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={() => setEditing(k)}
                        aria-label={`Change the name for ${r.from}`}>Change</button>
                    </div>
                  )}

              {r.unclear && (
                <label className="ml-field ml-import-names__amount">
                  <span className="ml-label">Amount</span>
                  <input className="ml-input" value={now.amount} placeholder="None" autoComplete="off"
                    aria-label={`Amount of ${r.from}`} onChange={e => set(k, { amount: e.target.value })} />
                </label>
              )}

              {r.categoryId == null && (
                <div className="ml-field">
                  <span className="ml-label">Which aisle?</span>
                  <CategoryPills categories={categories} value={now.categoryId} tintFor={tintFor}
                    onPick={id => set(k, { categoryId: id })} label={`Aisle for ${r.from}`} />
                </div>
              )}
            </li>
          )
        })}
      </ul>
      <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" onClick={done}>Done</button>
    </div>
  )
}
